import { useEffect, useState, type ReactNode } from "react";
import { ArrowLeft, RefreshCw } from "lucide-react";
import type { SupportLanguage } from "../appModel";
import { getAudioPreparationCopy, type WebRemoteAudioRequest } from "./webAudioAccess";

export function AudioReadyBoundary(props: {
  children: ReactNode;
  language: SupportLanguage;
  request: WebRemoteAudioRequest | null;
  isUnitReady(request: WebRemoteAudioRequest): Promise<boolean>;
  prepareUnit(request: WebRemoteAudioRequest): Promise<boolean>;
  onBack(): void;
  backLabel: string;
}) {
  const { request, isUnitReady, prepareUnit } = props;
  const [verifiedRequest, setVerifiedRequest] = useState<WebRemoteAudioRequest | null>(null);
  const [mountedRequest, setMountedRequest] = useState<WebRemoteAudioRequest | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [retry, setRetry] = useState(0);
  const copy = getAudioPreparationCopy(props.language);

  useEffect(() => {
    let cancelled = false;
    setBlocked(false);
    setVerifiedRequest(null);
    const verify = async () => {
      if (!request) return false;
      const cached = await isUnitReady(request);
      if (cancelled) return false;
      if (cached) return true;
      const prepared = await prepareUnit(request);
      if (cancelled) return false;
      return prepared && await isUnitReady(request);
    };
    void verify().then((ready) => {
      if (cancelled) return;
      if (ready) { setVerifiedRequest(request); setMountedRequest(request); }
      else setBlocked(true);
    }).catch(() => { if (!cancelled) setBlocked(true); });
    return () => { cancelled = true; };
  }, [request, isUnitReady, prepareUnit, retry]);

  const ready = Boolean(request && verifiedRequest === request);
  return (
    <>
    {request && mountedRequest === request ? <div data-audio-ready-content hidden={!ready} inert={!ready} style={ready ? { display: "contents" } : undefined}>{props.children}</div> : null}
    {!ready ? (
    <section className="view" role={blocked ? "alert" : "status"} aria-busy={!blocked}>
      <h2>{blocked ? copy.error : copy.checkingTitle}</h2>
      <p>{blocked ? copy.errorBody : copy.checkingBody}</p>
      {blocked ? (
        <div className="account-actions">
          <button type="button" className="primary-button" onClick={() => setRetry((value) => value + 1)}><RefreshCw aria-hidden="true" />{copy.tryAgain}</button>
          <button type="button" className="secondary-button" onClick={props.onBack}><ArrowLeft aria-hidden="true" />{props.backLabel}</button>
        </div>
      ) : <span className="loading-ring" aria-hidden="true" />}
    </section>
    ) : null}
    </>
  );
}
